import React, { useState } from "react";
import styles from "./SchoolFaq.module.css";
import { Minus, Plus } from "lucide-react";
import Image from "next/image";

import RichText from "../RichText/RichText";

interface ImageAsset {
  id?: number;
  url?: string;
  alternativeText?: string | null;
}

interface CardTitleAndDescriptionItem {
  id?: number;
  title?: string;
  description?: string;
  title_description?: {
    title?: string;
    description?: string;
  };
  images?: ImageAsset[];
}

interface CMSFAQ {
  id?: number;
  title: string;
  description: string;
  card_title_and_description?: CardTitleAndDescriptionItem[];
}

interface SchoolFaqProps {
  data?: {
    title?: string;
    questions?: CMSFAQ[];
  };
  grayDesign?: boolean;
}

const SchoolFaq: React.FC<SchoolFaqProps> = ({ data, grayDesign }) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const title = data?.title || "Frequently Asked Questions";
  const questions = data?.questions || [];

  return (
    <div className="container">
      <div className={styles.faqContainer}>
        <h2 className={grayDesign ? styles.grayDesignHeader : styles.heading}>
          {title}
        </h2>
        <div className={styles.faqList}>
          {questions?.map((item, index) => (
            <div
              key={item?.id || index}
              className={`${
                grayDesign ? styles.grayDesignFaqItem : styles.faqItem
              } ${activeIndex === index ? styles.active : ""}`}
              onClick={() => toggleFaq(index)}
            >
              <div className={styles.faqQuestion}>
                <h3>{item?.title}</h3>
                <span className={styles.icon}>
                  {activeIndex === index ? <Minus /> : <Plus />}
                </span>
              </div>
              {activeIndex === index && (
                <div className={styles.faqAnswer}>
                  {item?.description && <RichText data={item?.description} />}
                  {item?.card_title_and_description?.length ? (
                    <ul>
                      {item.card_title_and_description.map((ctd) => {
                        const titleText =
                          ctd?.title || ctd?.title_description?.title;
                        const descText =
                          ctd?.description ||
                          ctd?.title_description?.description;
                        return (
                          <li key={ctd?.id || `${item?.id}-ctd`}>
                            {titleText && <strong>{titleText}</strong>}
                            {descText && <p>{descText}</p>}
                            {ctd?.images?.length ? (
                              <div className={styles.faqImages}>
                                {ctd.images.map((img) => (
                                  <Image
                                    key={img?.id || `${titleText}-img`}
                                    src={img?.url || ""}
                                    alt={img?.alternativeText || "faq-image"}
                                    width={320}
                                    height={180}
                                    style={{
                                      width: "100%",
                                      height: "auto",
                                      maxWidth: 320,
                                    }}
                                  />
                                ))}
                              </div>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SchoolFaq;
